import type {
    InnateSpellGroupingInput,
    InnateSpellGroupResult,
} from '../types';
import { comparePhpStrings } from './php-json-values';

function isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function positiveMax(value: number | null): number {
    return value !== null && Number.isFinite(value) ? Math.max(1, Math.trunc(value)) : 1;
}

/** Multi-source grouping portion of finalize_creature_abilities()' innate-spell pass. */
export function groupInnateSpells(input: InnateSpellGroupingInput): InnateSpellGroupResult[] {
    type WorkingGroup = Omit<InnateSpellGroupResult, 'firstSource'> & { firstSource: string | null };
    const groups = new Map<string, WorkingGroup>();
    for (const entry of input.entries) {
        if (!isUuid(entry.spellId)) continue;
        const key = entry.spellId.toUpperCase();
        let group = groups.get(key);
        if (!group) {
            group = {
                spellId: entry.spellId,
                spellName: entry.spellName,
                firstSource: null,
                sourceMaxes: {},
                maxUses: 1,
            };
            groups.set(key, group);
        }

        const stacked = Object.entries(entry.stackedSourceMaxes);
        if (stacked.length > 0) {
            for (const [rawSource, rawMax] of stacked) {
                const source = rawSource.toUpperCase();
                group.sourceMaxes[source] = Math.max(group.sourceMaxes[source] ?? 0, positiveMax(rawMax));
                if (group.firstSource === null) group.firstSource = source;
            }
            continue;
        }

        const source = (entry.source !== '' ? entry.source : input.itemId).toUpperCase();
        group.sourceMaxes[source] = Math.max(group.sourceMaxes[source] ?? 0, positiveMax(entry.maxCharges));
        if (group.firstSource === null) group.firstSource = source;
    }

    for (const group of groups.values()) {
        group.maxUses = Math.max(1, Object.values(group.sourceMaxes).reduce((sum, value) => sum + value, 0));
        if (group.firstSource === null) group.firstSource = input.itemId;
    }
    const completed: InnateSpellGroupResult[] = [...groups.values()].map(group => ({
        ...group,
        firstSource: group.firstSource ?? input.itemId,
    }));
    return completed.sort((left, right) => comparePhpStrings(left.spellName, right.spellName));
}
